import React from 'react'
import { Check, Search, Loader2 } from 'lucide-react'
import type { GamingPlatformCardProps, GamerTagResult } from '@/features/enrollment'
import ReportProblemButton from '@/features/enrollment/campaigns/gaming-addiction/components/ReportProblemButton'

export default function GamingPlatformCard({
  platform,
  config,
  isSelected,
  enrollmentData,
  updateEnrollmentData,
  updatePlatformData,
  onCheckGamerTag,
  loadingStates,
  gamerTagResults,
  onReportProblem,
  reportedProblems = {}
}: GamingPlatformCardProps) {
  
  const handlePlatformToggle = () => {
    const currentPlatforms = enrollmentData.selectedPlatforms || []
    const newPlatforms = currentPlatforms.includes(platform) 
      ? currentPlatforms.filter((p: string) => p !== platform)
      : [...currentPlatforms, platform]
    updateEnrollmentData('selectedPlatforms', newPlatforms)
  }

  const renderGamerTagResults = (platform: string) => {
    const result = gamerTagResults[platform]
    if (!result) return null

    const isError = 'error' in result && result.error !== undefined
    const bgColor = isError ? 'bg-red-50' : (result.pass ? 'bg-green-50' : 'bg-red-50')
    const borderColor = isError ? 'border-red-200' : (result.pass ? 'border-green-200' : 'border-red-200')

    return (
      <div className={`mt-3 p-3 ${bgColor} rounded border ${borderColor}`}>
        <h4 className="font-medium text-sm mb-2">
          {isError ? 'Gamer Tag Verification Failed' : 'Gamer Tag Verification Results'}
        </h4>
        <div className="space-y-1 text-sm">
          <div className="flex justify-between">
            <span className="text-gray-600">Gamer Tag:</span>
            <span className="font-medium">{result.gamer_tag}</span>
          </div>
          {isError ? (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Error:</span>
                <span className="font-medium">{'error' in result ? result.error : ''}</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Details:</span>
                <span className="font-medium">{'details' in result ? result.details : ''}</span>
              </div>
              {onReportProblem && (
                <div className="mt-3 pt-2 border-t border-red-200">
                  <ReportProblemButton
                    onReportProblem={() => onReportProblem(platform)}
                    isReported={reportedProblems[platform] || false}
                    className="w-full justify-center"
                  />
                </div>
              )}
            </>
          ) : (
            <>
              <div className="flex justify-between">
                <span className="text-gray-600">Total Hours Played:</span>
                <span className="font-medium">{result.totalHoursPlayed.toLocaleString()} hours</span>
              </div>
              <div className="flex justify-between">
                <span className="text-gray-600">Status:</span>
                <span className={`font-medium ${result.pass ? 'text-green-600' : 'text-red-600'}`}>
                  {result.pass ? 'PASS' : 'FAIL'}
                </span>
              </div>
            </>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className={`card border-2 transition-colors cursor-pointer ${
        isSelected
          ? config.selectedBgColor
          : `${config.bgColor} ${config.borderColor} hover:${config.hoverBorderColor}`
      }`}
      onClick={handlePlatformToggle}
    >
      <div className="text-center mb-4">
        <div className={`w-full h-12 lg:h-16 ${config.color} text-white font-bold text-base lg:text-lg rounded-lg flex items-center justify-center mb-3 relative`}>
          {config.name}
          {isSelected && (
            <div className={`absolute -top-2 -right-2 w-6 h-6 ${config.color.replace('600', '500')} rounded-full flex items-center justify-center`}>
              <Check className="w-4 h-4 text-white" />
            </div>
          )}
        </div>
      </div>

      <div className="space-y-4">
        <div className="bg-white p-3 rounded border text-xs text-accent-700">
          <p className="font-semibold mb-1">Workflow:</p>
          {config.workflow.map((step, index) => (
            <p key={index}>{step}</p>
          ))}
        </div>

        {isSelected && (
          <div className="space-y-3">
            <div>
              <label className="font-medium text-accent-900 mb-3 text-sm lg:text-base">GamerTag</label>
              <div className="flex space-x-2">
                <input
                  type="text"
                  className="input-field flex-1"
                  placeholder="Enter GamerTag"
                  value={enrollmentData.platforms?.[platform]?.gamerTag || ''}
                  onChange={(e) => updatePlatformData(platform, 'gamerTag', e.target.value)}
                  onClick={(e) => e.stopPropagation()}
                />
                <button
                  className="btn-primary px-3 py-2 flex items-center space-x-1"
                  onClick={(e) => {
                    e.stopPropagation()
                    onCheckGamerTag(platform)
                  }}
                  disabled={loadingStates[platform]}
                >
                  {loadingStates[platform] ? (
                    <Loader2 className="w-4 h-4 animate-spin" />
                  ) : (
                    <Search className="w-4 h-4" />
                  )}
                  <span className="hidden sm:inline">Verify</span>
                </button>
              </div>
            </div>

            {renderGamerTagResults(platform)}
          </div>
        )}
      </div>
    </div>
  )
}
